import { useEffect, useState } from 'react'
import {
  FaArrowLeft,
  FaCheckCircle,
  FaSearch,
  FaUserFriends
} from 'react-icons/fa'

import { IUsers, useUser } from 'contexts/user.context'
import { trackEvent } from 'services/analytics'

import * as S from './style'

const UserSearch = () => {
  const [search, setSearch] = useState('')
  const [users, setUsers] = useState<IUsers[]>([])

  const { listUsers, user: currentUser } = useUser()

  useEffect(() => {
    ;(async () => {
      const response = await listUsers()
      setUsers(response.filter((user) => user.id !== currentUser?.id))
    })()
  }, [currentUser?.id, listUsers])

  const filteredUsers = users.filter((user) => {
    if (!search.trim()) return true

    const term = search.toLowerCase().trim()
    return (
      user.firstname.toLowerCase().includes(term) ||
      user.lastname.toLowerCase().includes(term) ||
      `${user.firstname} ${user.lastname}`.toLowerCase().includes(term)
    )
  })

  useEffect(() => {
    const term = search.trim()
    if (!term) return

    const timer = window.setTimeout(() => {
      trackEvent({
        eventType: 'search',
        entityType: 'user',
        value: term,
        metadata: {
          scope: 'users',
          resultCount: filteredUsers.length
        }
      })
    }, 700)

    return () => window.clearTimeout(timer)
  }, [filteredUsers.length, search])

  return (
    <S.Wrapper>
      <S.Container>
        <S.PageHeader>
          <S.HeaderIcon>
            <FaUserFriends />
          </S.HeaderIcon>
          <div>
            <S.Eyebrow>Comunidade RedSterna</S.Eyebrow>
            <S.Title>Encontre outros viajantes</S.Title>
            <S.Description>
              Descubra pessoas, conheça novas experiências e explore os roteiros
              compartilhados pela comunidade.
            </S.Description>
          </div>
        </S.PageHeader>

        <S.Toolbar>
          <S.SearchBox>
            <FaSearch />
            <S.Input
              type="search"
              placeholder="Buscar viajante por nome..."
              onChange={(event) => setSearch(event.target.value)}
              value={search}
              aria-label="Buscar viajante por nome"
            />
          </S.SearchBox>
          <S.ResultCount>
            <strong>{filteredUsers.length}</strong>{' '}
            {filteredUsers.length === 1 ? 'viajante' : 'viajantes'}
          </S.ResultCount>
        </S.Toolbar>

        {filteredUsers.length > 0 ? (
          <S.UserWrapper>
            {filteredUsers.map((user) => (
              <S.UserContainer
                key={user.id}
                to={{
                  pathname: `/usuario/${user.id}`,
                  search: `?user_id=${user.id}`,
                  state: { user }
                }}
              >
                <S.ProfileImageContainer>
                  <S.ProfileIcon
                    src={
                      user.profilePicture && user.profilePicture !== ''
                        ? user.profilePicture
                        : '/images/profile-empty.png'
                    }
                    alt={`${user.firstname} ${user.lastname}`}
                  />
                </S.ProfileImageContainer>

                <S.UserInfo>
                  <S.UserTitle>
                    {user.firstname} {user.lastname}
                    {user.verified && (
                      <S.VerifiedIcon title="Perfil verificado pela RedSterna">
                        <FaCheckCircle />
                      </S.VerifiedIcon>
                    )}
                  </S.UserTitle>
                  <S.UserSummary>
                    {user.about?.trim() ||
                      'Viajante da comunidade RedSterna. Conheça seu perfil e seus roteiros.'}
                  </S.UserSummary>
                  <S.ProfileCta>Ver perfil</S.ProfileCta>
                </S.UserInfo>
              </S.UserContainer>
            ))}
          </S.UserWrapper>
        ) : (
          <S.EmptyState>
            <FaSearch />
            <strong>Nenhum viajante encontrado</strong>
            <span>Tente buscar por outro nome.</span>
          </S.EmptyState>
        )}

        <S.BackButton to={currentUser?.id ? '/minha-conta' : '/'}>
          <FaArrowLeft />
          {currentUser?.id ? 'Voltar para meu perfil' : 'Voltar para o início'}
        </S.BackButton>
      </S.Container>
    </S.Wrapper>
  )
}

export default UserSearch
